/* eslint-disable no-undef */
/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { getCoachDetail } from '@/services/athena';
import { message } from 'antd';
import {
  AnchorNav,
  EmptyState,
  Tag,
  Avatar,
  Button,
  Spin,
  SubNav,
} from '../ui';
import CoachProfile from '../detail/components/coachProfile';
import {
  COACH_SECTIONS,
  sidOf,
} from '../detail/components/coachProfile/constants';
import powerChatUrl from '../utils/powerChat';
import styles from './member.less';
import detailStyles from '../detail/index.less';

/**
 * 成员 id：优先 coachId；**缺失时用带前缀的下标兜底**（不要裸下标，
 * 否则可能与真实 coachId 撞号，导致选中项错位）。
 * 抽到组件外是因为它要参与 useMemo 依赖，且必须是纯函数。
 */
const idOf = (coach, index) => {
  const { coachId } = coach || {};
  if (coachId !== undefined && coachId !== null && coachId !== '') {
    return String(coachId);
  }
  return `idx-${index}`;
};

/**
 * 商家成员（一级 tab）—— 成员名称 Tab（2026-09-18 结构调整）
 * ==================================================================
 * 成员名称直接作为 SubNav 的 Tab，仅展示当前成员详情。
 * 选中成员只在当前页面内切换，不写入 URL。
 *
 * 详情卡头部保留一个「商家会话」入口（原「Power 会话」，2026-10 改名 + 视觉降级）：
 * 仍走老 litta-h5 客服会话深链（powerChatUrl），逻辑不变，只是不再是主操作按钮。
 * 注意：顶部操作区另有新的「Power 会话」入口（HeaderActions），跳的是 palmer
 * Power 管理页；二者语义不同（商家会话=老客服会话，Power 会话=Power 管理），勿混。
 */
const MemberTab = (props) => {
  const { tenantId, initialCoachId } = props;
  const [coachList, setCoachList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoachId, setSelectedCoachId] = useState('');

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    getCoachDetail({ requestData: { tenantId } })
      .then((res) => {
        setLoading(false);
        if (res.code === '00000') {
          const nextList = res.data || [];
          setCoachList(nextList);
          // 深链定位：从完课总结详情页「所属商家」跳转过来时，默认选中本节课教练，
          // 而不是列表第一位（需求 7123729254）。idOf 口径与 SubNav 保持一致。
          const hitIndex = nextList.findIndex(
            (coach, index) =>
              initialCoachId && idOf(coach, index) === String(initialCoachId),
          );
          setSelectedCoachId(
            hitIndex >= 0
              ? idOf(nextList[hitIndex], hitIndex)
              : idOf(nextList[0], 0),
          );
          if (initialCoachId && hitIndex < 0) {
            /* 深链指定的教练已不在此商家当前成员列表（离职/换商家）。上面沿用本组件
               既有的「失效回退到第一位」行为，这里补一条可见提示，避免运营误读为定位成功
               （需求 7123729254 code review 意见）。 */
            message.info(
              `本节课教练（ID：${initialCoachId}）已不在该商家当前成员列表，已默认展示首位成员`,
            );
          }
        } else {
          message.error(res.msg || '加载成员列表失败');
        }
      })
      .catch(() => {
        /* 没有 catch 时请求失败会让 loading 一直挂着（白屏转圈） */
        setLoading(false);
        message.error('加载成员列表失败，请重试');
      });
  }, [tenantId]);

  /* 成员列表更新后，当前选中项失效时回退到第一位成员 */
  const effectiveId = useMemo(() => {
    const hit = coachList.some(
      (coach, index) => idOf(coach, index) === String(selectedCoachId),
    );
    return hit ? String(selectedCoachId) : idOf(coachList[0], 0);
  }, [coachList, selectedCoachId]);

  const current =
    coachList.find((coach, index) => idOf(coach, index) === effectiveId) ||
    coachList[0];

  if (loading) {
    return (
      <div className={detailStyles.tabBody}>
        <Spin dot />
      </div>
    );
  }

  if (!coachList.length) {
    return (
      <div className={detailStyles.tabBody}>
        <EmptyState description="该商家暂无教练成员" />
      </div>
    );
  }

  return (
    <div>
      <SubNav
        groups={coachList.map((coach, index) => ({
          value: idOf(coach, index),
          label: coach.coachNickName || coach.coachText || '未命名教练',
        }))}
        groupValue={effectiveId}
        onGroupChange={setSelectedCoachId}
      />
      <div className={detailStyles.tabBody}>
        <div className={styles.memberContentLayout}>
          <div className={styles.masterMain}>
            {current ? (
              <CoachProfile
                tenantId={tenantId}
                baseTags={[]}
                coachData={current}
                header={
                  <div className={styles.memberHead}>
                    <CoachTitle coach={current} />
                    <MerchantChatLink coach={current} />
                  </div>
                }
              />
            ) : (
              <EmptyState description="请选择一位成员" />
            )}
          </div>
          <AnchorNav
            className={styles.masterAnchor}
            items={COACH_SECTIONS.map((section) => ({
              key: sidOf(section.key),
              title: section.label,
            }))}
          />
        </div>
      </div>
    </div>
  );
};

/* ---------- 概览卡头部：头像 + 花名 + 类型标签 ---------- */
const CoachTitle = ({ coach }) => (
  <span className={styles.cardTitleRow}>
    <Avatar size={30} className={styles.cardAvatar}>
      {(coach.coachNickName || '?').slice(0, 1)}
    </Avatar>
    <span className={styles.coachName}>
      {coach.coachNickName || '未命名教练'}
    </span>
    {coach.typeName && (
      <Tag className={styles.typeTag} color="green">
        {coach.typeName}
      </Tag>
    )}
  </span>
);

/* ---------- 商家会话入口（原「Power 会话」，2026-10 改名 + 视觉降级） ----------
   · 文案：「Power 会话」→「商家会话」；
   · 视觉：描边次级按钮（Button type=default），与设计稿一致——旁路入口不抢主次；
   · 跳转逻辑不变，仍走老 litta-h5 客服会话深链。 */
const MerchantChatLink = ({ coach }) =>
  coach.encryptUserId ? (
    <a
      href={powerChatUrl({ encryptUserId: coach.encryptUserId })}
      target="_blank"
      rel="noopener noreferrer"
    >
      <Button type="default" size="small" className={styles.merchantChatBtn}>
        商家会话
      </Button>
    </a>
  ) : null;

export default MemberTab;
